import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiFetch from '../../../../utils/api';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  BarChart, Bar, Legend
} from 'recharts';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      apiFetch('/api/admin/dashboard').then(res => res.json()),
      apiFetch('/api/admin/dashboard/charts').then(res => res.json())
    ])
    .then(([dashData, chartsData]) => {
      setStats(dashData);
      setChartData(chartsData.timeline || []);
      setLoading(false);
    })
    .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="adm-dash"><div className="adm-dash__loader"><div className="adm-dash__spinner" /><span>Loading command center...</span></div></div>;
  if (!stats) return <div className="adm-dash"><div className="adm-dash__error">Failed to load dashboard data.</div></div>;

  const financials = stats.financials || {};
  const platformBalance = financials.platformBalance || 0;


  return (
    <div className="adm-dash">
      <div className="adm-dash__header">
        <div>
          <h1 className="adm-dash__title">Command Center</h1>
          <p className="adm-dash__subtitle">Real-time platform metrics & revenue tracking</p>
        </div>
        <div className="adm-dash__header-actions">
          <div className="adm-dash__live-badge">
            <span className="adm-dash__live-dot" /> LIVE SYSTEM
          </div>
        </div>
      </div>

      {/* KPI Row */}
      <div className="adm-dash__kpi-row">
        <div className="adm-kpi adm-kpi--featured" style={{ background: 'var(--awp-surface)', borderColor: 'var(--awp-accent-border)' }}>
          <div className="adm-kpi__icon">🏦</div>
          <div className="adm-kpi__data">
            <span className="adm-kpi__value" style={{ color: 'var(--awp-text-strong)' }}>Rs. {platformBalance.toLocaleString()}</span>
            <span className="adm-kpi__label" style={{ color: 'var(--awp-text-muted)' }}>Platform State</span>
          </div>
          <div className="adm-kpi__badge" style={{ background: 'var(--awp-surface-2)', color: 'var(--awp-text-subtle)' }}>live</div>
        </div>

        <div className="adm-kpi adm-kpi--purple" onClick={() => navigate('/dashboard/admin/users')} style={{cursor: 'pointer'}}>
          <div className="adm-kpi__icon">👥</div>
          <div className="adm-kpi__data">
            <span className="adm-kpi__value">{stats.users.active}</span>
            <span className="adm-kpi__label">Active Accounts</span>
          </div>
          <div className="adm-kpi__badge">engagement</div>
        </div>
        
        <div className="adm-kpi adm-kpi--blue" onClick={() => navigate('/dashboard/admin/pins-pending')} style={{cursor: 'pointer'}}>
          <div className="adm-kpi__icon">🎫</div>
          <div className="adm-kpi__data">
            <span className="adm-kpi__value">{stats.pins.pendingRequests}</span>
            <span className="adm-kpi__label">Pin Orders</span>
          </div>
          <div className="adm-kpi__badge">requests</div>
        </div>

        <div className="adm-kpi adm-kpi--teal">
          <div className="adm-kpi__icon">📈</div>
          <div className="adm-kpi__data">
            <span className="adm-kpi__value">+{stats.users.recentGrowth}</span>
            <span className="adm-kpi__label">24h Growth</span>
          </div>
          <div className="adm-kpi__badge">organic</div>
        </div>
      </div>



      {/* Charts Row */}
      <div className="adm-dash__charts-row">
        {/* User Growth Chart */}
        <div className="adm-chart-card">
          <div className="adm-chart-header">
            <h3>📈 30-Day User Growth</h3>
            <span>Registrations per day</span>
          </div>
          <div className="adm-chart-body">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--awp-accent)" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="var(--awp-accent)" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--awp-divider)" />
                <XAxis dataKey="date" tick={{fontSize: 12, fill: 'var(--awp-text-muted)'}} tickLine={false} axisLine={false} minTickGap={20} />
                <YAxis tick={{fontSize: 12, fill: 'var(--awp-text-muted)'}} tickLine={false} axisLine={false} />
                <RechartsTooltip contentStyle={{ background: 'var(--awp-surface)', borderRadius: '8px', border: '1px solid var(--awp-border)', boxShadow: 'var(--awp-shadow-md)' }} itemStyle={{ color: 'var(--awp-text)' }} />
                <Area type="monotone" dataKey="users" name="New Users" stroke="var(--awp-accent)" strokeWidth={3} fillOpacity={1} fill="url(#colorUsers)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Financial Flow Chart */}
        <div className="adm-chart-card">
          <div className="adm-chart-header">
            <h3>💰 30-Day Financial Flow</h3>
            <span>Revenue vs Withdrawals (PKR)</span>
          </div>
          <div className="adm-chart-body">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--awp-divider)" />
                <XAxis dataKey="date" tick={{fontSize: 12, fill: 'var(--awp-text-muted)'}} tickLine={false} axisLine={false} minTickGap={20} />
                <YAxis tickFormatter={(val) => `Rs. ${val.toLocaleString()}`} tick={{fontSize: 12, fill: 'var(--awp-text-muted)'}} tickLine={false} axisLine={false} />

                <RechartsTooltip cursor={{fill: 'var(--awp-surface-hover)'}} contentStyle={{ background: 'var(--awp-surface)', borderRadius: '8px', border: '1px solid var(--awp-border)', boxShadow: 'var(--awp-shadow-md)' }} itemStyle={{ color: 'var(--awp-text)' }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '13px', paddingTop: '10px' }} />
                <Bar dataKey="revenue" name="E-Pin Revenue" fill="var(--awp-success)" radius={[4, 4, 0, 0]} maxBarSize={40} />
                <Bar dataKey="withdrawals" name="Withdrawals" fill="var(--awp-danger)" radius={[4, 4, 0, 0]} maxBarSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Bottom Panels */}
      <div className="adm-dash__panels">
        {/* Withdrawals Overview */}
        <div className="adm-panel">
          <div className="adm-panel__head">
            <h3>📤 Withdrawal Pipeline</h3>
            <button className="adm-panel__link" onClick={() => navigate('/dashboard/admin/withdrawals-total')}>View All →</button>
          </div>
          <div className="adm-panel__metrics">
            <div className="adm-metric">
              <div className="adm-metric__bar adm-metric__bar--green" />
              <div className="adm-metric__info">
                <span className="adm-metric__label">Approved Volume</span>
                <span className="adm-metric__val">Rs. {stats.withdrawals.approved.toFixed(2)}</span>
              </div>
            </div>
            <div className="adm-metric pb-metric-action" onClick={() => navigate('/dashboard/admin/withdrawals-pending')}>
              <div className="adm-metric__bar adm-metric__bar--amber" />
              <div className="adm-metric__info">
                <span className="adm-metric__label">Action Required</span>
                <span className="adm-metric__val">{stats.withdrawals.pendingCount} Pending Requests</span>
              </div>
            </div>
            <div className="adm-metric">
              <div className="adm-metric__bar adm-metric__bar--red" />
              <div className="adm-metric__info">
                <span className="adm-metric__label">Rejected</span>
                <span className="adm-metric__val">{stats.withdrawals.rejectedCount} Requests</span>
              </div>
            </div>
          </div>
        </div>

        {/* E-Pin Overview */}
        <div className="adm-panel">
          <div className="adm-panel__head">
            <h3>🎫 E-Pin Pipeline</h3>
            <button className="adm-panel__link" onClick={() => navigate('/dashboard/admin/pins-available')}>Manage →</button>
          </div>
          <div className="adm-panel__metrics">
            <div className="adm-metric">
              <div className="adm-metric__bar adm-metric__bar--blue" />
              <div className="adm-metric__info">
                <span className="adm-metric__label">Active Pins Available</span>
                <span className="adm-metric__val">{stats.pins.available} Pins In Circulation</span>
              </div>
            </div>
            <div className="adm-metric pb-metric-action" onClick={() => navigate('/dashboard/admin/pins-pending')}>
              <div className="adm-metric__bar adm-metric__bar--amber" />
              <div className="adm-metric__info">
                <span className="adm-metric__label">Action Required</span>
                <span className="adm-metric__val">{stats.pins.pendingRequests} Purchase Requests</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="adm-panel">
          <div className="adm-panel__head">
            <h3>⚡ Quick Actions</h3>
          </div>
          <div className="adm-dash__actions">
            <button className="adm-action" onClick={() => navigate('/dashboard/admin/user-rewards')}>
              <div className="adm-action__icon-box amber">🏆</div>
              <div className="adm-action__text">
                <span className="adm-action__title">Review User Rewards</span>
                <span className="adm-action__sub">Verify and pay rank bonuses</span>
              </div>
              {stats.rewards.pendingCount > 0 && <span className="adm-action__count">{stats.rewards.pendingCount}</span>}
            </button>
            <button className="adm-action" onClick={() => navigate('/dashboard/admin/pins-pending')}>
              <div className="adm-action__icon-box purple">🎫</div>
              <div className="adm-action__text">
                <span className="adm-action__title">Review Pin Orders</span>
                <span className="adm-action__sub">Approve user pin buys</span>
              </div>
              {stats.pins.pendingRequests > 0 && <span className="adm-action__count">{stats.pins.pendingRequests}</span>}
            </button>
            <button className="adm-action" onClick={() => navigate('/dashboard/admin/users')}>
              <div className="adm-action__icon-box blue">👥</div>
              <div className="adm-action__text">
                <span className="adm-action__title">User Management</span>
                <span className="adm-action__sub">View all {stats.users.active} active accounts</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
